/**
 * TasksPage — "Moji nalozi"
 *
 * Simple list of work orders assigned to the current user.
 * Designed for meter readers and pump operators who don't have
 * access to the full Dispatcher Kanban board.
 */

import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { workordersApi, STATUSES, PRIORITIES, type WorkOrder } from '../api/workorders'
import { useAuthStore } from '../store/authStore'
import { Badge } from '../components/Badge'

// ── Helpers ───────────────────────────────────────────────────────────────────

function badge(list: { value: string; label: string; color: string }[], value: string) {
  const item = list.find((x) => x.value === value)
  return item ? <Badge className={item.color}>{item.label}</Badge> : null
}

function fmtDate(iso: string | null) {
  if (!iso) return null
  return new Date(iso).toLocaleDateString('sr-Latn-RS', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  })
}

// ── Status transition map: what can a field worker do? ───────────────────────

const NEXT: Record<string, { status: string; label: string; cls: string } | null> = {
  assigned:    { status: 'in_progress', label: 'Pokreni', cls: 'bg-brand-cyan hover:bg-brand-cyan-dark text-brand-midnight' },
  in_progress: { status: 'completed',   label: 'Završi',  cls: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
  pending:     null,
  completed:   null,
  cancelled:   null,
}

// ── Notes modal ───────────────────────────────────────────────────────────────

function NotesModal({
  onConfirm,
  onCancel,
  label,
}: {
  onConfirm: (notes: string) => void
  onCancel: () => void
  label: string
}) {
  const [notes, setNotes] = useState('')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/30" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="px-6 py-5 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">{label}</h3>
        </div>
        <div className="px-6 py-4">
          <label className="block text-xs font-medium text-gray-700 mb-1.5">
            Napomena (opciono)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder="Kratki opis…"
            className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-hidden focus:ring-2 focus:ring-blue-500 resize-none"
          />
        </div>
        <div className="px-6 pb-5 flex gap-2 justify-end">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Odustani
          </button>
          <button
            onClick={() => onConfirm(notes.trim())}
            className="px-4 py-2 text-sm rounded-lg bg-brand-cyan text-brand-midnight hover:bg-brand-cyan-dark transition-colors font-medium"
          >
            Potvrdi
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Work order card ───────────────────────────────────────────────────────────

function TaskCard({ wo }: { wo: WorkOrder }) {
  const qc = useQueryClient()
  const [modal, setModal] = useState<{ next: { status: string; label: string; cls: string } } | null>(null)

  const mutation = useMutation({
    mutationFn: ({ notes }: { notes: string }) =>
      workordersApi.updateStatus(wo.id, modal!.next.status, wo.version, notes || undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-tasks'] })
      setModal(null)
    },
  })

  const next = NEXT[wo.status] ?? null
  const priorityCls = wo.priority === 'urgent'
    ? 'border-l-4 border-l-red-500'
    : wo.priority === 'high'
    ? 'border-l-4 border-l-orange-400'
    : ''

  return (
    <>
      <div className={`bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden ${priorityCls}`}>
        <div className="px-5 py-4">
          <div className="flex items-start justify-between gap-3 mb-2">
            <h3 className="font-semibold text-gray-900 leading-tight">{wo.title}</h3>
            <div className="flex gap-1.5 shrink-0">
              {badge(PRIORITIES, wo.priority)}
              {badge(STATUSES, wo.status)}
            </div>
          </div>

          <p className="text-sm text-gray-600 mb-3">{wo.address}</p>

          {wo.description && (
            <p className="text-sm text-gray-500 mb-3 line-clamp-2">{wo.description}</p>
          )}

          <div className="flex flex-wrap gap-4 text-xs text-gray-400">
            {wo.scheduled_date && (
              <span>Planirano: <span className="text-gray-600 font-medium">{fmtDate(wo.scheduled_date)}</span></span>
            )}
            {wo.completed_date && (
              <span>Završeno: <span className="text-gray-600 font-medium">{fmtDate(wo.completed_date)}</span></span>
            )}
            {wo.notes && (
              <span className="truncate max-w-xs">Napomena: {wo.notes}</span>
            )}
          </div>
        </div>

        {next && (
          <div className="px-5 pb-4 pt-0">
            <button
              onClick={() => setModal({ next })}
              disabled={mutation.isPending}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 ${next.cls}`}
            >
              {mutation.isPending ? 'Čuvanje…' : next.label}
            </button>
            {mutation.isError && (
              <span className="ml-3 text-xs text-red-500">
                {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? 'Greška'}
              </span>
            )}
          </div>
        )}
      </div>

      {modal && (
        <NotesModal
          label={`${modal.next.label}: ${wo.title}`}
          onConfirm={(notes) => mutation.mutate({ notes })}
          onCancel={() => setModal(null)}
        />
      )}
    </>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function TasksPage() {
  const { user } = useAuthStore()
  const [filter, setFilter] = useState<'active' | 'done'>('active')

  const { data, isLoading, isError } = useQuery({
    queryKey: ['my-tasks', user?.id],
    queryFn: () =>
      workordersApi.list({ assigned_to: user!.id, limit: 200 }).then((r) => r.data),
    enabled: !!user?.id,
    refetchInterval: 60_000,
  })

  const active = data?.filter((w) => ['pending', 'assigned', 'in_progress'].includes(w.status)) ?? []
  const done   = data?.filter((w) => ['completed', 'cancelled'].includes(w.status)) ?? []

  const shown = filter === 'active' ? active : done

  return (
    <div className="p-6 space-y-5 max-w-3xl">
      {/* Filter tabs */}
      <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          onClick={() => setFilter('active')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            filter === 'active' ? 'bg-white shadow-xs text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Aktivni
          {active.length > 0 && (
            <span className="ml-1.5 text-xs bg-blue-100 text-brand-cyan-text font-semibold px-1.5 py-0.5 rounded-full">
              {active.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setFilter('done')}
          className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
            filter === 'done' ? 'bg-white shadow-xs text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Završeni
          {done.length > 0 && (
            <span className="ml-1.5 text-xs bg-gray-200 text-gray-600 font-semibold px-1.5 py-0.5 rounded-full">
              {done.length}
            </span>
          )}
        </button>
      </div>

      {/* Content */}
      {isLoading && (
        <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
          Učitavanje…
        </div>
      )}

      {isError && (
        <div className="flex items-center justify-center h-48 text-red-500 text-sm">
          Greška pri učitavanju naloga.
        </div>
      )}

      {!isLoading && !isError && shown.length === 0 && (
        <div className="flex items-center justify-center h-48 text-gray-400 text-sm">
          {filter === 'active' ? 'Nemate aktivnih naloga.' : 'Nemate završenih naloga.'}
        </div>
      )}

      <div className="space-y-4">
        {shown.map((wo) => (
          <TaskCard key={wo.id} wo={wo} />
        ))}
      </div>
    </div>
  )
}
